import React, { useState, useEffect } from 'react';
import { StatusBadge } from '../components/Card';
import DateRangePicker from '../components/DateRangePicker';
import { ChevronLeftIcon, ChevronDownIcon, ChevronRightIcon, PlusIcon, CalendarIcon } from '../icons/FinanceIcons';
import { getDatePresetLabel } from '../data/mockData';
import { cashupService } from '../../../_services';
import { firstRefundTotals, refundCountLabel, minusMoney } from '../../refunds/refundLogic';
import { siteColour } from '../../../_services/sites.service';
import moment from 'moment';
import {
  getUserInfo,
  isUserSelfEmployed,
  formatDate,
  formatLastEditTime
} from '../../cashup/CashupUtils';
import useWindowSize from '../../new-service/useWindowSize';

// Cash Up View Component
const CashUpView = ({ staffFilter = 'all', salonIds = null, selectedSites = [] }) => {
  // A till is per site, so the list shows which one when the filter spans sites.
  const multiSite = !!(salonIds && selectedSites.length > 1);
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const [selectedSession, setSelectedSession] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateStart, setDateStart] = useState(moment().startOf('month').toDate());
  const [dateEnd, setDateEnd] = useState(moment().endOf('month').toDate());
  const [datePreset, setDatePreset] = useState('mtd');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    cashActual: '',
    cardActual: '',
    otherActual: '',
    beuTerminal: '',
    cardOnFile: '',
    deposits: '',
    paymentLink: '',
    klarna: '',
    clearpay: '',
    closingFloat: '',
    cashToBank: '',
    notes: ''
  });

  const [sessions, setSessions] = useState([]);

  // Fetch cash up history from API
  const fetchCashupHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const { salonId, user } = getUserInfo();

      const params = {
        page: 1,
        limit: 50,
        startDate: moment(dateStart).format('YYYY-MM-DD'),
        endDate: moment(dateEnd).format('YYYY-MM-DD'),
        salonId: salonId,
        // Locations filter: a till stays per site, so this lists each site's
        // cash-up for the period rather than merging them into one sheet.
        ...(salonIds ? { salonIds } : {})
      };

      // Set cashupType based on employment type
      if (isUserSelfEmployed()) {
        params.cashupType = 2;
        params.hairdresserId = user._id;
      } else {
        params.cashupType = 1;
        // Apply staff filter if not 'all'
        if (staffFilter !== 'all') {
          params.hairdresserId = staffFilter;
        }
      }

      const response = await cashupService.getCashupHistory(params);

      if (response.data && response.data.status === 1) {
        const cashupData = response.data.data.cashups || [];
        // Transform API data to UI format
        const transformedSessions = cashupData.map(transformCashupToSession);
        setSessions(transformedSessions);
      } else {
        console.error("Error fetching cashup history:", response.data);
        setSessions([]);
      }
    } catch (err) {
      console.error("Error fetching cashup history:", err);
      setError("Failed to load cash up history");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  // Transform API cashup object to UI session format
  const transformCashupToSession = (cashup) => {
    const date = moment(cashup.date);

    // Handle cashedUpBy - it can be a string, an object with name, or undefined
    let cashedUpByName = 'Unknown';
    if (cashup.cashedUpByName) {
      cashedUpByName = cashup.cashedUpByName;
    } else if (cashup.cashedUpBy) {
      if (typeof cashup.cashedUpBy === 'string') {
        cashedUpByName = cashup.cashedUpBy;
      } else if (typeof cashup.cashedUpBy === 'object' && cashup.cashedUpBy.name) {
        cashedUpByName = cashup.cashedUpBy.name;
      }
    }

    return {
      id: cashup._id,
      _id: cashup._id,
      date: formatDate(cashup.date),
      dateShort: date.format('DD.MM.YYYY'),
      status: cashup.status === 1 ? 'complete' : 'incomplete',
      cashedUpBy: cashedUpByName,
      // Which location's till this is — shown when the filter spans sites.
      salonId: cashup.salonId || null,
      salonName: cashup.salonName || '',
      notes: cashup.notes || null,
      lastEdit: formatLastEditTime(cashup.lastEditTime || cashup.updatedAt),
      cashExpected: cashup.payments?.cash?.expected || 0,
      cardExpected: cashup.payments?.card?.expected || 0,
      otherExpected: cashup.payments?.other?.expected || 0,
      // Cash paid back to clients that day (docs/refunds.md: cash refunds come
      // out of the drawer). Assumed payments.cash.refunds, or refunds.cash.
      cashRefunds: firstRefundTotals(cashup.payments?.cash?.refunds, cashup.refunds?.cash, cashup.cashRefunds),
      cashActual: cashup.payments?.cash?.actual,
      cardActual: cashup.payments?.card?.actual,
      otherActual: cashup.payments?.other?.actual,
      beuTerminal: cashup.beuPayments?.terminal || 0,
      cardOnFile: cashup.beuPayments?.cardOnFile || 0,
      deposits: cashup.beuPayments?.deposits || 0,
      paymentLink: cashup.beuPayments?.paymentLink || 0,
      klarna: cashup.beuPayments?.klarna || 0,
      clearpay: cashup.beuPayments?.clearpay || 0,
      // Multi-location: gift cards honoured across sites today (read-only, not in totals).
      groupVouchers: cashup.groupVouchers || null,
      closingFloat: cashup.endOfDay?.closingFloat || 0,
      cashToBank: cashup.endOfDay?.cashToBank || 0,
      rawData: cashup // Keep original data for updates
    };
  };

  // Fetch data on mount and when date range or staff filter changes
  useEffect(() => {
    fetchCashupHistory();
  }, [dateStart, dateEnd, staffFilter, salonIds]);

  const handleOpenSession = async (session) => {
    setLoading(true);
    setError(null);

    try {
      const cashupId = session._id || session.id;
      const response = await cashupService.getCashupById(cashupId);

      if (response.data && response.data.status === 1) {
        const fullCashup = response.data.data;
        const fullSession = transformCashupToSession(fullCashup);

        setSelectedSession(fullSession);
        setFormData({
          cashActual: fullSession.cashActual?.toString() || '',
          cardActual: fullSession.cardActual?.toString() || '',
          otherActual: fullSession.otherActual?.toString() || '',
          beuTerminal: fullSession.beuTerminal?.toString() || '',
          cardOnFile: fullSession.cardOnFile?.toString() || '',
          deposits: fullSession.deposits?.toString() || '',
          paymentLink: fullSession.paymentLink?.toString() || '',
          klarna: fullSession.klarna?.toString() || '',
          clearpay: fullSession.clearpay?.toString() || '',
          closingFloat: fullSession.closingFloat?.toString() || '',
          cashToBank: fullSession.cashToBank?.toString() || '',
          notes: fullSession.notes || ''
        });
      } else {
        console.error("Error fetching cashup details:", response.data);
        setError("Failed to load cash up details");
      }
    } catch (err) {
      console.error("Error fetching cashup details:", err);
      setError("Failed to load cash up details");
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewCashUp = async () => {
    setSaving(true);
    setError(null);

    try {
      const { salonId, user } = getUserInfo();

      // Create cashup with all required fields according to API model
      const newCashup = {
        date: moment().format('YYYY-MM-DD'),
        salonId: salonId,
        cashHolder: 1
      };

      // Set cashupType based on employment type
      if (isUserSelfEmployed()) {
        newCashup.cashupType = 2;
        newCashup.hairdresserId = user._id;
        newCashup.cashHolder = 2;
      } else {
        newCashup.cashupType = 1;
      }

      const response = await cashupService.createCashup(newCashup);

      if (response.data && response.data.status === 1) {
        const createdCashup = response.data.data;
        const newSession = transformCashupToSession(createdCashup);

        setSelectedSession(newSession);
        setFormData({
          cashActual: '',
          cardActual: '',
          otherActual: '',
          beuTerminal: '',
          cardOnFile: '',
          deposits: '',
          paymentLink: '',
          klarna: '',
          clearpay: '',
          closingFloat: '',
          cashToBank: '',
          notes: ''
        });
        setIsCreating(true);
      } else {
        console.error("Error creating cashup:", response.data);
        setError("Failed to create cash up: " + (response.data?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error creating cashup:", err);
      const errorMessage = err.response?.data?.message || err.message || "Unknown error";
      setError("Failed to create cash up: " + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const calculateTotals = () => {
    const cash = parseFloat(formData.cashActual) || 0;
    const card = parseFloat(formData.cardActual) || 0;
    const other = parseFloat(formData.otherActual) || 0;
    const beuTerminal = parseFloat(formData.beuTerminal) || 0;
    const cardOnFile = parseFloat(formData.cardOnFile) || 0;
    const deposits = parseFloat(formData.deposits) || 0;
    const paymentLink = parseFloat(formData.paymentLink) || 0;
    const klarna = parseFloat(formData.klarna) || 0;
    const clearpay = parseFloat(formData.clearpay) || 0;

    const paymentTotal = cash + card + other;
    const beuPayTotal = beuTerminal + cardOnFile + deposits + paymentLink + klarna + clearpay;
    const grandTotal = paymentTotal + beuPayTotal;

    return { paymentTotal, beuPayTotal, grandTotal };
  };

  const getVariance = (actual, expected) => {
    const act = parseFloat(actual) || 0;
    const exp = parseFloat(expected) || 0;
    return act - exp;
  };

  const handleComplete = async () => {
    if (!selectedSession?._id && !selectedSession?.id) {
      setError("No session selected");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const cashupId = selectedSession._id || selectedSession.id;

      // Prepare data in API format
      const updateData = {
        payments: {
          cash: {
            expected: selectedSession.cashExpected || 0,
            actual: parseFloat(formData.cashActual) || 0
          },
          card: {
            expected: selectedSession.cardExpected || 0,
            actual: parseFloat(formData.cardActual) || 0
          },
          other: {
            expected: selectedSession.otherExpected || 0,
            actual: parseFloat(formData.otherActual) || 0
          }
        },
        beuPayments: {
          terminal: parseFloat(formData.beuTerminal) || 0,
          cardOnFile: parseFloat(formData.cardOnFile) || 0,
          deposits: parseFloat(formData.deposits) || 0,
          paymentLink: parseFloat(formData.paymentLink) || 0,
          klarna: parseFloat(formData.klarna) || 0,
          clearpay: parseFloat(formData.clearpay) || 0
        },
        endOfDay: {
          closingFloat: parseFloat(formData.closingFloat) || 0,
          cashToBank: parseFloat(formData.cashToBank) || 0
        },
        notes: formData.notes || ''
      };

      const response = await cashupService.completeCashup(cashupId, updateData);

      if (response.data && response.data.status === 1) {
        // Refresh the list to show updated data
        await fetchCashupHistory();
        setSelectedSession(null);
        setIsCreating(false);
      } else {
        console.error("Error completing cashup:", response.data);
        setError("Failed to complete cash up: " + (response.data?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error completing cashup:", err);
      const errorMessage = err.response?.data?.message || err.message || "Unknown error";
      setError("Failed to complete cash up: " + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle saving without completing (draft save)
  const handleSave = async () => {
    if (!selectedSession?._id && !selectedSession?.id) {
      setError("No session selected");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const cashupId = selectedSession._id || selectedSession.id;

      // Prepare data in API format
      const updateData = {
        payments: {
          cash: {
            expected: selectedSession.cashExpected || 0,
            actual: parseFloat(formData.cashActual) || 0
          },
          card: {
            expected: selectedSession.cardExpected || 0,
            actual: parseFloat(formData.cardActual) || 0
          },
          other: {
            expected: selectedSession.otherExpected || 0,
            actual: parseFloat(formData.otherActual) || 0
          }
        },
        beuPayments: {
          terminal: parseFloat(formData.beuTerminal) || 0,
          cardOnFile: parseFloat(formData.cardOnFile) || 0,
          deposits: parseFloat(formData.deposits) || 0,
          paymentLink: parseFloat(formData.paymentLink) || 0,
          klarna: parseFloat(formData.klarna) || 0,
          clearpay: parseFloat(formData.clearpay) || 0
        },
        endOfDay: {
          closingFloat: parseFloat(formData.closingFloat) || 0,
          cashToBank: parseFloat(formData.cashToBank) || 0
        },
        notes: formData.notes || ''
      };

      const response = await cashupService.updateCashup(cashupId, updateData);

      if (response.data && response.data.status === 1) {
        // Update local state
        setSessions(prev => prev.map(s =>
          (s._id === cashupId || s.id === cashupId)
            ? { ...s, ...formData, lastEdit: 'Just now' }
            : s
        ));
      } else {
        console.error("Error saving cashup:", response.data);
        setError("Failed to save cash up: " + (response.data?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Error saving cashup:", err);
      const errorMessage = err.response?.data?.message || err.message || "Unknown error";
      setError("Failed to save cash up: " + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Detail/Form View
  if (selectedSession) {
    const totals = calculateTotals();
    const cashVariance = getVariance(formData.cashActual, selectedSession.cashExpected);
    const cardVariance = getVariance(formData.cardActual, selectedSession.cardExpected);
    const otherVariance = getVariance(formData.otherActual, selectedSession.otherExpected);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
        {/* Error Display */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '12px' : '0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => { setSelectedSession(null); setIsCreating(false); setError(null); }}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                border: '1px solid #e5e5e5',
                backgroundColor: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                flexShrink: 0
              }}
            >
              <ChevronLeftIcon size={20} />
            </button>
            <div>
              <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>Cash Up</h2>
              <p style={{ fontSize: '14px', color: '#737373' }}>{selectedSession.date}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => { setSelectedSession(null); setIsCreating(false); setError(null); }}
              disabled={saving}
              style={{
                padding: isMobile ? '10px 14px' : '10px 20px',
                borderRadius: '8px',
                border: '1px solid #e5e5e5',
                backgroundColor: 'white',
                fontSize: '14px',
                fontWeight: '500',
                color: '#737373',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: isMobile ? '10px 14px' : '10px 20px',
                borderRadius: '8px',
                border: '1px solid #e5e5e5',
                backgroundColor: 'white',
                fontSize: '14px',
                fontWeight: '500',
                color: '#007EA0',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={handleComplete}
              disabled={saving}
              style={{
                padding: isMobile ? '10px 14px' : '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: saving ? '#9ca3af' : '#007EA0',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {saving ? 'Completing...' : 'Complete'}
            </button>
          </div>
        </div>

        {/* Cash Up Form */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', padding: isMobile ? '16px' : '24px' }}>
          {/* Payment Types Section */}
          <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' }}>Payment Types</h3>

            {isMobile ? (
              /* Mobile: Stacked cards for each payment type */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Cash', key: 'cashActual', expected: selectedSession.cashExpected, variance: cashVariance },
                  { label: 'Card', key: 'cardActual', expected: selectedSession.cardExpected, variance: cardVariance },
                  { label: 'Other', key: 'otherActual', expected: selectedSession.otherExpected, variance: otherVariance }
                ].map(({ label, key, expected, variance }) => (
                  <div key={key} style={{ padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>{label}</span>
                      <div style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: variance === 0 ? '#d1fae5' : variance > 0 ? '#dbeafe' : '#fee2e2',
                        color: variance === 0 ? '#059669' : variance > 0 ? '#1d4ed8' : '#dc2626'
                      }}>
                        {variance >= 0 ? '+' : ''}£{variance.toFixed(2)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Actual</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#737373', fontSize: '14px' }}>£</span>
                          <input
                            type="number"
                            value={formData[key]}
                            onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e5e5', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Expected</span>
                        <div style={{ padding: '8px 10px', backgroundColor: '#f0f0f0', borderRadius: '6px', fontSize: '14px', color: '#737373' }}>
                          £{expected?.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop: Grid layout */
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px 100px', gap: '16px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e5e5' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase' }}>Type</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase' }}>Actual</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase' }}>Expected</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase' }}>Variance</span>
                </div>
                {[
                  { label: 'Cash', key: 'cashActual', expected: selectedSession.cashExpected, variance: cashVariance },
                  { label: 'Card', key: 'cardActual', expected: selectedSession.cardExpected, variance: cardVariance },
                  { label: 'Other', key: 'otherActual', expected: selectedSession.otherExpected, variance: otherVariance }
                ].map(({ label, key, expected, variance }) => (
                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px 100px', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '14px', color: '#1a1a1a' }}>{label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#737373' }}>£</span>
                      <input
                        type="number"
                        value={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '14px' }}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={{ padding: '10px 12px', backgroundColor: '#f5f5f5', borderRadius: '8px', fontSize: '14px', color: '#737373' }}>
                      £{expected?.toFixed(2)}
                    </div>
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: variance === 0 ? '#d1fae5' : variance > 0 ? '#dbeafe' : '#fee2e2',
                      color: variance === 0 ? '#059669' : variance > 0 ? '#1d4ed8' : '#dc2626',
                      textAlign: 'center'
                    }}>
                      {variance >= 0 ? '+' : ''}£{variance.toFixed(2)}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Cash refunds: paid out of the till today */}
          {selectedSession.cashRefunds && (
            <div style={{ marginTop: isMobile ? '-12px' : '-16px', marginBottom: isMobile ? '24px' : '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                <span style={{ fontSize: '14px', color: '#1a1a1a' }}>
                  Cash refunds
                  {selectedSession.cashRefunds.count ? <span style={{ color: '#737373' }}> · {refundCountLabel(selectedSession.cashRefunds.count)}</span> : null}
                </span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#b42318' }}>{minusMoney(selectedSession.cashRefunds.amount)}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#737373', margin: '6px 0 0' }}>Paid back to clients from the till today. Expected cash is after these.</p>
            </div>
          )}

          {/* Beu Pay Section */}
          <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' }}>Beu Pay</h3>

            <div style={{ display: isMobile ? 'flex' : 'grid', flexDirection: 'column', gridTemplateColumns: '1fr 140px', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Beu Terminal', key: 'beuTerminal' },
                  { label: 'Card on File', key: 'cardOnFile' },
                  { label: 'Deposits', key: 'deposits' },
                  { label: 'Payment Link', key: 'paymentLink' },
                  { label: 'Klarna', key: 'klarna' },
                  { label: 'Clearpay', key: 'clearpay' }
                ].map(({ label, key }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#1a1a1a' }}>{label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#737373' }}>£</span>
                      <input
                        type="number"
                        value={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                        style={{ width: '100px', padding: '8px 12px', border: '1px solid #e5e5e5', borderRadius: '6px', fontSize: '14px', textAlign: 'right' }}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
                  <p style={{ fontSize: '12px', color: '#737373', marginBottom: '4px' }}>Beu Pay Total</p>
                  <p style={{ fontSize: '20px', fontWeight: '600', color: '#007EA0' }}>£{totals.beuPayTotal.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Gift cards across locations — informational; money owed between sites, never in this till's totals */}
          {selectedSession?.groupVouchers && (selectedSession.groupVouchers.honouredForOthers.count > 0 || selectedSession.groupVouchers.honouredByOthers.count > 0) && (
            <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>Gift cards from other locations</h3>
              <p style={{ fontSize: '12px', color: '#737373', margin: '0 0 12px' }}>Not part of this till's totals. Money owed between your locations. The full ledger is under All locations.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedSession.groupVouchers.honouredForOthers.bySite.map((r) => (
                  <div key={`for-${r.salonId}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#1a1a1a' }}>Honoured here, sold at {r.name} <span style={{ color: '#737373' }}>· {r.count} {r.count === 1 ? 'redemption' : 'redemptions'}</span></span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#15803d' }}>+£{Number(r.amount).toFixed(2)} owed to you</span>
                  </div>
                ))}
                {selectedSession.groupVouchers.honouredByOthers.bySite.map((r) => (
                  <div key={`by-${r.salonId}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#1a1a1a' }}>Sold here, honoured at {r.name} <span style={{ color: '#737373' }}>· {r.count} {r.count === 1 ? 'redemption' : 'redemptions'}</span></span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#B14438' }}>−£{Number(r.amount).toFixed(2)} you owe</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* End of Day Section */}
          <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' }}>End of Day</h3>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '16px' }}>
              {[
                { label: 'Closing Float', key: 'closingFloat' },
                { label: 'Cash to Bank', key: 'cashToBank' }
              ].map(({ label, key }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#1a1a1a' }}>{label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#737373' }}>£</span>
                    <input
                      type="number"
                      value={formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                      style={{ width: '100px', padding: '8px 12px', border: '1px solid #e5e5e5', borderRadius: '6px', fontSize: '14px', textAlign: 'right' }}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grand Total */}
          <div style={{
            padding: isMobile ? '16px' : '20px',
            backgroundColor: '#007EA0',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <span style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Grand Total</span>
            <span style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '700', color: 'white' }}>£{totals.grandTotal.toFixed(2)}</span>
          </div>

          {/* Notes */}
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', marginBottom: '12px' }}>Notes</h3>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes about this cash up..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px 16px',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
      {/* Error Display */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc2626',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 4px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: isMobile ? '12px' : '0' }}>
        <div>
          <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>Cash Up</h2>
          <p style={{ fontSize: '14px', color: '#737373' }}>Daily cash reconciliation and end of day reports</p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          {/* Date Range */}
          <div style={{ position: 'relative', flex: isMobile ? 1 : 'none' }}>
            <button
              onClick={() => setDatePickerOpen(!datePickerOpen)}
              disabled={loading || saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: 'white',
                border: '1px solid #e5e5e5',
                borderRadius: '10px',
                fontSize: '14px',
                cursor: (loading || saving) ? 'not-allowed' : 'pointer',
                opacity: (loading || saving) ? 0.5 : 1,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              <CalendarIcon size={16} />
              <span style={{ color: '#007EA0', fontWeight: '500' }}>{getDatePresetLabel(datePreset)}</span>
              <ChevronDownIcon size={16} />
            </button>

            <DateRangePicker
              isOpen={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              startDate={dateStart}
              endDate={dateEnd}
              selectedPreset={datePreset}
              onPresetChange={setDatePreset}
              align="right"
              onApply={(start, end) => {
                setDateStart(start);
                setDateEnd(end);
              }}
            />
          </div>

          {/* New Cash Up Button */}
          <button
            onClick={handleStartNewCashUp}
            disabled={loading || saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: (loading || saving) ? '#9ca3af' : '#007EA0',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              cursor: (loading || saving) ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            <PlusIcon size={16} />
            {saving ? 'Creating...' : 'New Cash Up'}
          </button>
        </div>
      </div>

      {/* Sessions */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ fontSize: '15px', color: '#737373' }}>Loading cash up sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ fontSize: '15px', color: '#737373' }}>No cash up sessions found for this period</p>
          </div>
        ) : isMobile ? (
          /* Mobile: Card-based list */
          <div className="forms-mobile-list">
            {sessions.map((session) => (
              <div
                key={session.id || session._id}
                className="forms-mobile-card"
                onClick={() => handleOpenSession(session)}
              >
                <div className="forms-mobile-card-left">
                  <div className="forms-mobile-card-info">
                    <span className="forms-mobile-card-name">{session.date}</span>
                    <span className="forms-mobile-card-meta">
                      {session.cashedUpBy}
                      {session.notes && (
                        <>
                          <span className="forms-mobile-card-dot"> · </span>
                          {session.notes}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                <div className="forms-mobile-card-right">
                  <StatusBadge status={session.status} />
                  <ChevronRightIcon size={16} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop: Table view */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e5e5e5' }}>
                <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
                {multiSite && <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</th>}
                <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cashed Up By</th>
                <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</th>
                <th style={{ textAlign: 'left', padding: '14px 20px', fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Edit</th>
                <th style={{ padding: '14px 20px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session, idx) => (
                <tr
                  key={session.id || session._id}
                  style={{
                    borderBottom: idx < sessions.length - 1 ? '1px solid #f0f0f0' : 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.1s'
                  }}
                  onClick={() => handleOpenSession(session)}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '16px 20px' }}>
                    <StatusBadge status={session.status} />
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>{session.date}</p>
                  </td>
                  {multiSite && (
                    <td style={{ padding: '16px 20px' }}>
                      <p style={{ fontSize: '14px', color: '#4b5563', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '999px', marginRight: '8px', verticalAlign: 'middle', background: siteColour(session.salonId) }} />
                        {session.salonName || 'None'}
                      </p>
                    </td>
                  )}
                  <td style={{ padding: '16px 20px' }}>
                    <p style={{ fontSize: '14px', color: '#4b5563' }}>{session.cashedUpBy}</p>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <p style={{ fontSize: '14px', color: session.notes ? '#4b5563' : '#9ca3af' }}>
                      {session.notes || 'N/A'}
                    </p>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <p style={{ fontSize: '13px', color: '#9ca3af' }}>{session.lastEdit}</p>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <ChevronRightIcon size={16} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CashUpView;
